import { useRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

// `brand` resuelve a var(--primary-color): respeta el theming runtime del CMS.
// Estilos alineados con el design-system del portal (design-system/components.css .btn-*).
const VARIANTS = {
  primary: {
    base: 'bg-brand border-[1.5px] border-brand text-white shadow-primary lg:shadow-none',
    hover: 'hover:bg-brand-light hover:border-brand-light hover:text-white',
  },
  'outline-primary': {
    base: 'bg-transparent border-[1.5px] border-brand text-brand',
    hover: 'hover:bg-brand hover:text-white',
  },
  secondary: {
    base: 'bg-white border-[1.5px] border-brand text-brand',
    hover: 'hover:bg-primary-50',
  },
  'outline-secondary': {
    base: 'bg-transparent border-[1.5px] border-brand text-brand',
    hover: 'hover:bg-primary-50',
  },
  danger: {
    base: 'bg-danger border-[1.5px] border-danger text-white',
    hover: 'hover:bg-white hover:text-danger',
  },
  'outline-danger': {
    base: 'bg-transparent border-[1.5px] border-danger text-danger',
    hover: 'hover:bg-danger hover:text-white',
  },
  link: {
    base: 'bg-transparent border-0 text-brand underline w-auto shadow-none',
    hover: 'hover:text-brand-light',
  },
};

// normal-case gana siempre a `uppercase` (orden interno del plugin de Tailwind),
// así xs/sm nunca van en mayúsculas, igual que el legacy.
// Desktop (lg:) alinea con Material Design 3: altura fija + padding MD3 para que
// todos los botones midan igual y el label centre (py-0 deja mandar a h-*).
const SIZES = {
  xs: 'p-[5px] text-xs normal-case lg:h-8 lg:py-0 lg:px-4 lg:text-sm',
  sm: 'p-[7px] text-sm normal-case lg:h-10 lg:py-0 lg:px-5',
  md: 'px-6 py-2 text-[0.9375rem] lg:h-11 lg:py-0 lg:px-6 lg:text-base',
  lg: 'px-7 py-2.5 text-lg lg:h-14 lg:py-0 lg:px-8',
};

// En desktop: tipografía label-large MD3, sin "lift" y con state layer (overlay
// bg-current: aclara en filled, tinta en outlined/text) en hover/pressed.
const BASE = 'relative inline-flex items-center justify-center gap-2 w-full md:w-auto rounded-pill font-semibold text-center outline-none transition duration-150 hover:-translate-y-0.5 disabled:opacity-55 disabled:cursor-not-allowed disabled:translate-y-0 lg:font-medium lg:tracking-[0.02em] lg:leading-none lg:hover:translate-y-0 lg:overflow-hidden lg:after:absolute lg:after:inset-0 lg:after:rounded-[inherit] lg:after:bg-current lg:after:opacity-0 lg:after:transition-opacity lg:hover:after:opacity-[0.08] lg:active:after:opacity-[0.12]';

const LOADING = "overflow-hidden cursor-wait pointer-events-none bg-brand-light text-white before:content-[''] before:absolute before:top-0 before:left-0 before:h-full before:w-[150%] before:bg-[repeating-linear-gradient(60deg,transparent,transparent_0.75rem,var(--primary-color)_0.75rem,var(--primary-color)_1.5rem)] before:animate-btn-load";

// Para DOM externo (formularios Zoho) que no puede renderizar <Button>.
export const buttonClasses = (type = 'primary', size = 'md', extra = '') => {
  const variant = VARIANTS[type] ?? VARIANTS.primary;
  return classNames(
    BASE,
    variant.base,
    variant.hover,
    SIZES[size] ?? SIZES.md,
    type !== 'link' && 'no-underline',
    extra,
  );
};

function Button(props) {
  const {
    to = null,
    onClick = null,
    text = null,
    type = 'primary',
    className = '',
    disabled = false,
    loading = false,
    children = null,
    isSubmitBtn = false,
    isIconButton = false,
    limitToOneClick = false,
    size = 'md',
    linkProps = {},
    uppercase = false,
    fullWidth = false,
    ...rest
  } = props;

  const timesClicked = useRef(0);

  const handleBtnClick = (e) => {
    if (limitToOneClick && timesClicked.current) return;

    if (onClick && !loading) {
      onClick(e);
      timesClicked.current += 1;
    }
  };

  const variant = VARIANTS[type] ?? VARIANTS.primary;
  // 'no-hover-btn' es la API legacy para botones (de icono) sin efecto hover.
  const noHover = isIconButton || /\bno-hover-btn\b/.test(className);

  const classList = classNames(
    BASE,
    variant.base,
    !noHover && !loading && variant.hover,
    SIZES[size] ?? SIZES.md,
    type !== 'link' && 'no-underline',
    {
      'px-1': isIconButton,
      uppercase,
      'lg:w-full': fullWidth,
    },
    loading && LOADING,
    className,
  );

  const btnContent = (
    <span className='relative z-[2] inline-flex items-center justify-center gap-2'>
      {text && (<span>{text}</span>)}
      {/* Ignore children if a text prop is passed */}
      {children && !text && children}
    </span>
  );

  if (to && !disabled) {
    return (
      <Link
        to={to}
        onClick={handleBtnClick}
        className={classList}
        disabled={disabled}
        {...linkProps}
        {...rest}
      >
        {btnContent}
      </Link>
    );
  }

  return (
    <button
      type={isSubmitBtn ? 'submit' : 'button'}
      onClick={handleBtnClick}
      className={classList}
      disabled={disabled || loading}
      {...rest}
    >
      {btnContent}
    </button>
  );
}

Button.displayName = 'Button';
Button.propTypes = {
  to: PropTypes.string,
  onClick: PropTypes.func,
  text: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  type: PropTypes.oneOf(['primary', 'outline-primary', 'secondary', 'outline-secondary', 'danger', 'outline-danger', 'link']),
  className: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  children: PropTypes.node,
  isSubmitBtn: PropTypes.bool,
  isIconButton: PropTypes.bool,
  limitToOneClick: PropTypes.bool,
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),
  linkProps: PropTypes.object,
  uppercase: PropTypes.bool,
  fullWidth: PropTypes.bool,
};

export default Button;
